import React, { useState } from "react";
import { validateEmail } from "../utils/utils";
import Button from "./Button";
import CustomChekbox from "./CustomChekbox";
import ReCAPTCHA from "react-google-recaptcha";
import { RECAPTCHA_KEY, googleMapKey } from "../env";
import axios from "axios";
import { toast } from "react-toastify";
import Calendar, { DateObject } from "react-multi-date-picker";
import { usePlacesWidget } from "react-google-autocomplete";

const PersonalDetails = ({
  personalDetails,
  setPersonalDetails,
  goNext,
  purchaseInformation
}) => {
  const [captchaVerification, setCaptchaVerification] = useState(null);
  const [captchaError, setCaptchaError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [validationErrors, setValidationErrors] = useState({
    firstNameError: "",
    lastNameError: "",
    emailError: "",
    dobError: "",
    consentCheckedError: "",
    postalAddressError: ""
  });

  const handleDobChange = (val) => {
    setValidationErrors((prevState) => ({
      ...prevState,
      dobError: ""
    }));
    setPersonalDetails((prevState) => ({
      ...prevState,
      dob: val
    }));
  };

  const { ref } = usePlacesWidget({
    apiKey: googleMapKey,
    onPlaceSelected: (place) => {
      setPersonalDetails((prevState) => ({
        ...prevState,
        postalAddress: place.formatted_address
      }));
    },
    options: {
      componentRestrictions: { country: "au" },
      fields: ["address_components", "formatted_address", "geometry", "name"],
      types: ["address"]
    }
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    let isValid = true;

    const {
      firstName,
      lastName,
      consentChecked,
      futurePromotions,
      phoneNumber,
      email
    } = personalDetails;
    const { receipt, receiptDate } = purchaseInformation;

    if (!firstName) {
      setValidationErrors((prevState) => ({
        ...prevState,
        firstNameError: "This field is required"
      }));
      isValid = false;
    }

    if (!lastName) {
      setValidationErrors((prevState) => ({
        ...prevState,
        lastNameError: "This field is required"
      }));
      isValid = false;
    }

    if (!phoneNumber) {
      setValidationErrors((prevState) => ({
        ...prevState,
        phoneNumberError: "This field is required"
      }));
      isValid = false;
    }

    if (phoneNumber && phoneNumber.length < 9) {
      setValidationErrors((prevState) => ({
        ...prevState,
        phoneNumberError: "Phone number must be at least 9 digits"
      }));
      isValid = false;
    }

    if (!personalDetails.dob) {
      setValidationErrors((prevState) => ({
        ...prevState,
        dobError: "This field is required"
      }));
      isValid = false;
    }

    if (personalDetails.dob) {
      if (
        personalDetails.dob.year &&
        personalDetails.dob.year.toString().length !== 4
      ) {
        setValidationErrors((prevState) => ({
          ...prevState,
          dobError: "Date of birth format is not valid!"
        }));

        isValid = false;
      }

      if (personalDetails.dob.year > 2005) {
        setValidationErrors((prevState) => ({
          ...prevState,
          dobError: "You must be 18 to enter the competition!"
        }));

        isValid = false;
      }
    }

    if (!personalDetails.postalAddress) {
      setValidationErrors((prevState) => ({
        ...prevState,
        postalAddressError: "This field is required"
      }));
      isValid = false;
    }

    if (!validateEmail(email)) {
      setValidationErrors((prevState) => ({
        ...prevState,
        emailError: "Email address format not valid!"
      }));
      isValid = false;
    }

    if (!consentChecked) {
      setValidationErrors((prevState) => ({
        ...prevState,
        consentCheckedError: "This field is required"
      }));
      isValid = false;
    }

    if (!captchaVerification) {
      setCaptchaError("This field must be checked");
      isValid = false;
    }

    if (isValid) {
      try {
        setIsLoading(true);

        const formData = new FormData();
        formData.append("receipt", receipt);
        const fileUpload = await axios.post(
          "/api/moov/upload-receipt",
          formData
        );
        const payload = {
          uploaded_receipt: fileUpload.data.file_name,
          first_name: firstName,
          last_name: lastName,
          email,
          date_of_birth: personalDetails.dob.format("YYYY-MM-DD"),
          mobile: phoneNumber,
          postal_address: personalDetails.postalAddress,
          date_of_purchase: receiptDate.format("YYYY-MM-DD"),
          is_i_can_confirm: consentChecked ? 1 : 0,
          is_please_let_me_know: futurePromotions ? 1 : 0
        };

        await axios.post("/api/moov/form", payload);
        setIsLoading(false);
        goNext();
      } catch (error) {
        setIsLoading(false);

        if (
          error.response &&
          error.response.data &&
          error.response.data.message
        ) {
          toast.error(error.response.data.message);
        } else {
          toast.error("Something went wrong. Try again!");
        }
      }
    }
  };

  const handleVerify = (token) => {
    setCaptchaVerification(token);
    setCaptchaError("");
  };

  const handleChange = (name, value) => {
    setPersonalDetails((prevState) => ({
      ...prevState,
      [name]: value
    }));
  };

  return (
    <div className="w-full mt-6">
      <form onSubmit={handleSubmit}>
        <div className="w-full flex flex-col md:flex-row gap-6 mb-8">
          <div className="flex flex-col w-full relative">
            <label className="text-sm text-secondary tracking-[0.25px] font-bold mb-1">
              First name*
            </label>
            <input
              type="text"
              name="firstName"
              value={personalDetails.firstName}
              onChange={(e) => {
                setValidationErrors((prevState) => ({
                  ...prevState,
                  firstNameError: ""
                }));
                handleChange("firstName", e.target.value.trimStart());
              }}
              className={
                "block w-full text-sm text-secondary text-secondary placeholder:text-grey border  placeholder:text-xs pl-4 pr-10 h-[40px] rounded-[6px] shadow-sm focus:border-primary outline-0 sm:text-sm " +
                (validationErrors.firstNameError
                  ? "border-red-600"
                  : "border-grey")
              }
              placeholder="Enter your first name"
            />
            <svg
              width="13"
              height="14"
              viewBox="0 0 13 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="absolute right-4 top-9"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M1.02513 9.42513C1.6815 8.76875 2.57174 8.4 3.5 8.4H9.1C10.0283 8.4 10.9185 8.76875 11.5749 9.42513C12.2313 10.0815 12.6 10.9717 12.6 11.9V13.3C12.6 13.6866 12.2866 14 11.9 14C11.5134 14 11.2 13.6866 11.2 13.3V11.9C11.2 11.343 10.9788 10.8089 10.5849 10.4151C10.1911 10.0212 9.65695 9.8 9.1 9.8H3.5C2.94305 9.8 2.4089 10.0212 2.01508 10.4151C1.62125 10.8089 1.4 11.343 1.4 11.9V13.3C1.4 13.6866 1.0866 14 0.7 14C0.313401 14 0 13.6866 0 13.3V11.9C0 10.9717 0.368749 10.0815 1.02513 9.42513Z"
                fill="#999999"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M6.3 1.4C5.1402 1.4 4.2 2.3402 4.2 3.5C4.2 4.6598 5.1402 5.6 6.3 5.6C7.4598 5.6 8.4 4.6598 8.4 3.5C8.4 2.3402 7.4598 1.4 6.3 1.4ZM2.8 3.5C2.8 1.567 4.367 0 6.3 0C8.233 0 9.8 1.567 9.8 3.5C9.8 5.433 8.233 7 6.3 7C4.367 7 2.8 5.433 2.8 3.5Z"
                fill="#999999"
              />
            </svg>
            {validationErrors.firstNameError && (
              <p className="text-xs text-red-600 mt-2">
                {validationErrors.firstNameError}
              </p>
            )}
          </div>
          <div className="flex flex-col w-full relative">
            <label className="text-sm text-secondary tracking-[0.25px] font-bold mb-1">
              Last name*
            </label>
            <input
              type="text"
              name="lastName"
              value={personalDetails.lastName}
              onChange={(e) => {
                setValidationErrors((prevState) => ({
                  ...prevState,
                  lastNameError: ""
                }));
                handleChange("lastName", e.target.value.trimStart());
              }}
              className={
                "block w-full text-sm text-secondary placeholder:text-grey placeholder:text-xs pl-4 pr-10 h-[40px] rounded-[6px] border   shadow-sm focus:border-primary outline-0 sm:text-sm " +
                (validationErrors.lastNameError
                  ? "border-red-600"
                  : "border-grey")
              }
              placeholder="Enter your last name"
            />
            <svg
              width="13"
              height="14"
              viewBox="0 0 13 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="absolute right-4 top-9"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M1.02513 9.42513C1.6815 8.76875 2.57174 8.4 3.5 8.4H9.1C10.0283 8.4 10.9185 8.76875 11.5749 9.42513C12.2313 10.0815 12.6 10.9717 12.6 11.9V13.3C12.6 13.6866 12.2866 14 11.9 14C11.5134 14 11.2 13.6866 11.2 13.3V11.9C11.2 11.343 10.9788 10.8089 10.5849 10.4151C10.1911 10.0212 9.65695 9.8 9.1 9.8H3.5C2.94305 9.8 2.4089 10.0212 2.01508 10.4151C1.62125 10.8089 1.4 11.343 1.4 11.9V13.3C1.4 13.6866 1.0866 14 0.7 14C0.313401 14 0 13.6866 0 13.3V11.9C0 10.9717 0.368749 10.0815 1.02513 9.42513Z"
                fill="#999999"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M6.3 1.4C5.1402 1.4 4.2 2.3402 4.2 3.5C4.2 4.6598 5.1402 5.6 6.3 5.6C7.4598 5.6 8.4 4.6598 8.4 3.5C8.4 2.3402 7.4598 1.4 6.3 1.4ZM2.8 3.5C2.8 1.567 4.367 0 6.3 0C8.233 0 9.8 1.567 9.8 3.5C9.8 5.433 8.233 7 6.3 7C4.367 7 2.8 5.433 2.8 3.5Z"
                fill="#999999"
              />
            </svg>
            {validationErrors.lastNameError && (
              <p className="text-xs text-red-600 mt-2">
                {validationErrors.lastNameError}
              </p>
            )}
          </div>
        </div>

        <div className="w-full flex flex-col md:flex-row gap-6 mb-8">
          <div className="flex flex-col w-full relative">
            <div className="flex flex-wrap items-center sm:space-x-1 mb-1">
              <label className="text-sm text-secondary tracking-[0.25px] font-bold ">
                Date of birth*
              </label>
            </div>
            <Calendar
              currentDate={
                new DateObject({
                  year: 2005,
                  month: 1,
                  day: 1
                })
              }
              format={"DD/MM/YYYY"}
              placeholder={"DD/MM/YYYY"}
              numberOfMonths={1}
              range={false}
              maxDate={"2005/12/31"}
              value={personalDetails.dob}
              onChange={handleDobChange}
              style={{
                width: "100%",
                boxShadow: `0 1px 2px 0 rgba(0,0,0,0.05)`,
                height: "40px",
                borderColor: validationErrors.dobError ? "red" : "#999999",
                borderRadius: "6px",
                fontSize: "14px",
                padding: "3px 10px",
                cursor: "pointer"
              }}
            />
            {validationErrors.dobError && (
              <p className="text-xs text-red-600 mt-1">
                {validationErrors.dobError}
              </p>
            )}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="#999"
              className="w-4 h-5 absolute top-[34px] right-[14px]"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5m-9-6h.008v.008H12v-.008zM12 15h.008v.008H12V15zm0 2.25h.008v.008H12v-.008zM9.75 15h.008v.008H9.75V15zm0 2.25h.008v.008H9.75v-.008zM7.5 15h.008v.008H7.5V15zm0 2.25h.008v.008H7.5v-.008zm6.75-4.5h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V15zm0 2.25h.008v.008h-.008v-.008zm2.25-4.5h.008v.008H16.5v-.008zm0 2.25h.008v.008H16.5V15z"
              />
            </svg>
          </div>
          <div className="flex flex-col w-full relative">
            <label className="text-sm text-secondary tracking-[0.25px] font-bold mb-1">
              Email*
            </label>
            <input
              type="text"
              name="email"
              value={personalDetails.email}
              onChange={(e) => {
                setValidationErrors((prevState) => ({
                  ...prevState,
                  emailError: ""
                }));
                handleChange("email", e.target.value.trimStart());
              }}
              className={
                "block w-full text-sm text-secondary placeholder:text-grey placeholder:text-xs pl-4 pr-10 h-[40px] rounded-[6px] border  shadow-sm focus:border-primary outline-0 sm:text-sm " +
                (validationErrors.emailError ? "border-red-600" : "border-grey")
              }
              placeholder="Enter your email address"
            />
            {validationErrors.emailError && (
              <p className="text-xs text-red-600 mt-1">
                {validationErrors.emailError}
              </p>
            )}
            <svg
              width="14"
              height="12"
              viewBox="0 0 14 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="absolute right-4 top-[38px]"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M1.34055 1.62524L7 5.58686L12.6595 1.62524C12.5542 1.41713 12.3378 1.27273 12.0909 1.27273H1.90909C1.66221 1.27273 1.44579 1.41713 1.34055 1.62524ZM12.7273 3.13133L7.36493 6.88497C7.14582 7.03834 6.85418 7.03834 6.63507 6.88497L1.27273 3.13133V9.54545C1.27273 9.894 1.56054 10.1818 1.90909 10.1818H12.0909C12.4395 10.1818 12.7273 9.894 12.7273 9.54545V3.13133ZM0 1.90909C0 0.857637 0.857637 0 1.90909 0H12.0909C13.1424 0 14 0.857637 14 1.90909V9.54545C14 10.5969 13.1424 11.4545 12.0909 11.4545H1.90909C0.857637 11.4545 0 10.5969 0 9.54545V1.90909Z"
                fill="#999999"
              />
            </svg>
          </div>
          <div className="flex flex-col w-full relative">
            <label className="text-sm text-secondary tracking-[0.25px] font-bold mb-1">
              Mobile phone number*
            </label>
            <input
              type="tel"
              name="tel"
              value={personalDetails.phoneNumber}
              onChange={(e) => {
                setValidationErrors((prevState) => ({
                  ...prevState,
                  phoneNumberError: ""
                }));
                handleChange("phoneNumber", e.target.value.trimStart());
              }}
              className={
                "block w-full text-sm text-secondary placeholder:text-grey placeholder:text-xs pl-4 pr-10 h-[40px] rounded-[6px] border  shadow-sm focus:border-primary outline-0 sm:text-sm " +
                (validationErrors.phoneNumberError
                  ? "border-red-600"
                  : "border-grey")
              }
              placeholder="Enter your phone number"
            />
            {validationErrors.phoneNumberError && (
              <p className="text-xs text-red-600 mt-1">
                {validationErrors.phoneNumberError}
              </p>
            )}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="#999999"
              className="absolute w-4 h-4 right-3.5 text-primary top-[36px]"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3"
              />
            </svg>
          </div>
        </div>

        <div className="w-full flex gap-6 mb-6">
          <div className="flex flex-col w-full relative">
            <div className="flex flex-wrap items-center sm:space-x-1 mb-1">
              <label className="text-sm text-secondary tracking-[0.25px] font-bold ">
                Address*{" "}
              </label>
            </div>
            <div className="relative">
              <input
                ref={ref}
                type="text"
                name="postalAddress"
                defaultValue={personalDetails.postalAddress}
                onChange={(e) => {
                  setValidationErrors((prevState) => ({
                    ...prevState,
                    postalAddressError: ""
                  }));
                  handleChange("postalAddress", e.target.value.trimStart());
                }}
                className={
                  "block w-full text-sm text-secondary placeholder:text-grey placeholder:text-xs pl-4 pr-10 h-[40px] rounded-[6px] border  shadow-sm focus:border-primary outline-0 sm:text-sm " +
                  (validationErrors.postalAddressError
                    ? "border-red-600"
                    : "border-grey")
                }
                placeholder="Enter your address..."
              />
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="#999999"
                className="w-5 h-5 absolute right-3 top-2.5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z"
                />
              </svg>
            </div>
            {validationErrors.postalAddressError && (
              <p className="text-xs text-red-600 mt-2">
                {validationErrors.postalAddressError}
              </p>
            )}
          </div>
        </div>

        <div className="mb-4 mt-8">
          <CustomChekbox
            label="I can confirm I have provided accurate information and adhere to the promotion terms and conditions."
            value="consentChecked"
            isChecked={personalDetails.consentChecked}
            onChange={() => {
              handleChange("consentChecked", !personalDetails.consentChecked);
              setValidationErrors((prevState) => ({
                ...prevState,
                consentCheckedError: ""
              }));
            }}
          />
          {validationErrors.consentCheckedError && (
            <p className="text-xs text-red-600 mt-1">
              This field must be checked
            </p>
          )}
        </div>
        <CustomChekbox
          label="Please let me know about any future MOOV offers and special promotions."
          value="futurePromotions"
          isChecked={personalDetails.futurePromotions}
          onChange={() =>
            handleChange("futurePromotions", !personalDetails.futurePromotions)
          }
        />
        <div className="flex items-center space-x-2 justify-center mb-6 mt-12">
          <ReCAPTCHA sitekey={RECAPTCHA_KEY} onChange={handleVerify} />
        </div>
        <div className="flex">
          {captchaError && (
            <span className="text-red-500 text-xs mx-auto mb-2 -mt-4">
              {captchaError}
            </span>
          )}
        </div>
        <Button
          type="submit"
          title={"Submit"}
          isLoading={isLoading}
          disabled={isLoading}
          additonalClasses="mt-8"
        />
      </form>
    </div>
  );
};

export default PersonalDetails;
